/**
 * Error model.
 *
 * Two audiences, one table:
 *  - REST callers get an HTTP status plus `{ reason | message, code }` shaped like Agora's
 *    RTC REST API (`{"message":"invalid appid"}`) and Cloud Recording (`{"reason":"...","code":N}`).
 *  - SDK facade callers get the numeric `sdkCode` used by the Agora RTC SDK error model so a
 *    migration path exists for apps moving off Agora.
 *
 * Numeric SDK codes intentionally mirror the documented Agora ERR_* values for the subset we
 * surface. Anything we cannot map stays ERR_FAILED (-1) rather than inventing a number.
 */
function def(code, httpStatus, category, sdkCode, message, retryable = false) {
    return { code, httpStatus, category, sdkCode, message, retryable };
}
/**
 * Registry. Keep POST/PUT semantics first, then validation, then infra.
 * HTTP statuses follow the RTC REST "Response status codes" reference:
 * 400 bad request, 401 unauthorized, 403 forbidden, 404 not found, 415 media type,
 * 429 too many requests, 500 internal.
 */
export const ERRORS = {
    ok: def('ok', 200, 'internal', 0, 'Success.'),
    // --- credentials / auth -----------------------------------------------------
    invalid_appid: def('invalid_appid', 401, 'auth', 101, 'invalid appid'),
    invalid_credentials: def('invalid_credentials', 401, 'auth', 110, 'invalid credentials'),
    missing_authorization: def('missing_authorization', 401, 'auth', 110, 'missing Authorization header'),
    // HTTP 401（不是 403）：会话无效/过期、MFA 挑战无效属「认证失败」，语义修正见
    // docs/qa/http-unauthorized-401.md。sdkCode 9 是 Agora 对标基线，保持不变。
    unauthorized: def('unauthorized', 401, 'auth', 9, 'authentication required'),
    app_disabled: def('app_disabled', 403, 'auth', 101, 'app is disabled'),
    service_not_enabled: def('service_not_enabled', 403, 'auth', -1, 'service is not activated for this project'),
    // --- tokens -----------------------------------------------------------------
    invalid_token: def('invalid_token', 401, 'auth', 110, 'invalid token'),
    token_expired: def('token_expired', 401, 'auth', 109, 'token expired'),
    token_not_yet_valid: def('token_not_yet_valid', 401, 'auth', 110, 'token not yet valid'),
    token_channel_mismatch: def('token_channel_mismatch', 401, 'auth', 110, 'token does not match channel'),
    token_uid_mismatch: def('token_uid_mismatch', 401, 'auth', 110, 'token does not match uid'),
    token_privilege_expired: def('token_privilege_expired', 401, 'auth', 109, 'token privilege expired'),
    // --- channel / room ---------------------------------------------------------
    invalid_channel_name: def('invalid_channel_name', 400, 'validation', 102, 'invalid channel name'),
    invalid_parameter: def('invalid_parameter', 400, 'validation', -2, 'invalid parameter'),
    unsupported_media_type: def('unsupported_media_type', 415, 'validation', -4, 'unsupported media type'),
    channel_not_found: def('channel_not_found', 404, 'not_found', 113, 'channel not found'),
    user_not_found: def('user_not_found', 404, 'not_found', 113, 'user not in channel'),
    no_server_resources: def('no_server_resources', 503, 'upstream', 103, 'no server resources'),
    join_rejected: def('join_rejected', 403, 'auth', 17, 'join channel rejected'),
    banned: def('banned', 403, 'auth', 17, 'user is banned by a kicking rule'),
    already_in_channel: def('already_in_channel', 409, 'conflict', 19, 'participant already in channel'),
    // --- recording --------------------------------------------------------------
    recording_not_found: def('recording_not_found', 404, 'not_found', -1, 'recording task not found'),
    recording_invalid_state: def('recording_invalid_state', 409, 'conflict', 8, 'recording task is not in a valid state for this operation'),
    recording_quota_exceeded: def('recording_quota_exceeded', 429, 'quota', 22, 'recording concurrency quota exceeded'),
    recording_upstream_error: def('recording_upstream_error', 502, 'upstream', -1, 'egress upstream error'),
    // --- streaming --------------------------------------------------------------
    streaming_not_found: def('streaming_not_found', 404, 'not_found', -1, 'streaming task not found'),
    streaming_invalid_state: def('streaming_invalid_state', 409, 'conflict', 8, 'streaming task is not in a valid state for this operation'),
    streaming_quota_exceeded: def('streaming_quota_exceeded', 429, 'quota', 22, 'streaming concurrency quota exceeded'),
    streaming_upstream_error: def('streaming_upstream_error', 502, 'upstream', -1, 'rtmp upstream error'),
    // --- platform ---------------------------------------------------------------
    project_not_found: def('project_not_found', 404, 'not_found', -1, 'project not found'),
    project_exists: def('project_exists', 409, 'conflict', -1, 'project already exists'),
    webhook_not_found: def('webhook_not_found', 404, 'not_found', -1, 'webhook endpoint not found'),
    collaborator_not_found: def('collaborator_not_found', 404, 'not_found', -1, 'collaborator not found'),
    outbox_not_found: def('outbox_not_found', 404, 'not_found', -1, 'outbox event not found'),
    rate_limited: def('rate_limited', 429, 'rate_limit', 12, 'too many requests'),
    upstream_unavailable: def('upstream_unavailable', 503, 'upstream', 111, 'upstream service unavailable'),
    internal_error: def('internal_error', 500, 'internal', -1, 'internal server error'),
    // --- rtc provider bridge (IM contract) --------------------------------------
    rtc_provider_unavailable: def('RTC_PROVIDER_UNAVAILABLE', 503, 'upstream', 111, 'RTC provider unavailable'),
    rtc_provider_disabled: def('RTC_PROVIDER_DISABLED', 503, 'upstream', -1, 'RTC provider is disabled by configuration'),
    rtc_token_denied: def('RTC_TOKEN_DENIED', 403, 'auth', 17, 'RTC token denied'),
    rtc_token_invalid_request: def('RTC_TOKEN_INVALID_REQUEST', 400, 'validation', -2, 'invalid RTC token request'),
    rtc_uid_mapping_invalid_request: def('RTC_UID_MAPPING_INVALID_REQUEST', 400, 'validation', -2, 'invalid RTC uid mapping request'),
};
/** Error carrying a registry code, safe to serialise to a client. */
export class ApiError extends Error {
    definition;
    httpStatus;
    code;
    sdkCode;
    details;
    /** Extra headers (e.g. Retry-After, X-Request-Id echo). */
    headers;
    constructor(code, message, options) {
        const definition = ERRORS[code];
        super(message ?? definition.message);
        this.name = 'ApiError';
        this.code = definition.code;
        this.definition = definition;
        this.httpStatus = definition.httpStatus;
        this.sdkCode = definition.sdkCode;
        if (options?.details)
            this.details = options.details;
        if (options?.headers)
            this.headers = options.headers;
    }
    toJSON() {
        const body = {
            code: this.code,
            message: this.message,
            // Agora Cloud Recording uses `reason`; RTC REST uses `message`. Emit both so either
            // integration style can read the failure without special-casing our gateway.
            reason: this.message,
            // Agora client error numbers are negative; emit the same sign as the IDL (`errors.json`) and
            // the SDK facades so a REST caller and an SDK caller compare the same number.
            sdkCode: this.sdkCode === 0 ? 0 : -Math.abs(this.sdkCode),
        };
        if (this.details)
            body.details = this.details;
        return body;
    }
}
/** Agora SDK-compatible numeric codes for the subsets surfaced by `@nimeet/rtc-web`. */
export const SdkErrorCode = {
    ERR_OK: 0,
    ERR_FAILED: -1,
    ERR_INVALID_ARGUMENT: -2,
    ERR_NOT_READY: -3,
    ERR_NOT_SUPPORTED: -4,
    ERR_REFUSED: -5,
    ERR_NOT_INITIALIZED: -7,
    ERR_INVALID_STATE: -8,
    ERR_NO_PERMISSION: -9,
    ERR_TIMEDOUT: -10,
    ERR_JOIN_CHANNEL_REJECTED: -17,
    ERR_LEAVE_CHANNEL_REJECTED: -18,
    ERR_ALREADY_IN_USE: -19,
    ERR_INVALID_APP_ID: -101,
    ERR_INVALID_CHANNEL_NAME: -102,
    ERR_NO_SERVER_RESOURCES: -103,
    ERR_TOKEN_EXPIRED: -109,
    ERR_INVALID_TOKEN: -110,
    ERR_CONNECTION_INTERRUPTED: -111,
    ERR_CONNECTION_LOST: -112,
    ERR_NOT_IN_CHANNEL: -113,
};
export function isApiError(value) {
    return value instanceof ApiError;
}
//# sourceMappingURL=errors.js.map